/**
 * API Configuration
 * Centralized URL configuration for production-safe environment handling
 */

const isDev = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Get API URL with proper fallback strategy
 * - Production: Uses REACT_APP_API_URL (required)
 * - Development: Uses REACT_APP_API_URL or localhost:3000/api
 */
export const getApiUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  
  if (isProduction && !apiUrl) {
    // Fallback to HTTPS Railway URL in production
    return 'https://munasati-platform-production.up.railway.app/api';
  }
  
  // Ensure HTTPS in production, even if URL is provided
  if (isProduction && apiUrl) {
    return apiUrl.startsWith('https://') ? apiUrl : `https://${apiUrl.replace(/^https?:\/\//, '')}`;
  }
  
  return apiUrl || (isDev ? 'http://localhost:3000/api' : undefined);
};

/**
 * Get Server Base URL with proper fallback strategy
 * - Production: Uses REACT_APP_SERVER_URL (required)
 * - Development: Uses REACT_APP_SERVER_URL or localhost:3000
 */
export const getServerUrl = () => {  
  // Production check with HTTPS fallback
  if (isProduction && process.env.REACT_APP_SERVER_URL) {
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    return serverUrl.startsWith('https://') ? serverUrl : `https://${serverUrl.replace(/^https?:\/\//, '')}`;
  }
  
  // Production fallback to HTTPS Railway URL
  if (isProduction) {
    return 'https://munasati-platform-production.up.railway.app';
  }
  
  // Development fallback
  if (isDev) {
    return process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';
  }
  
  // Default fallback
  return process.env.REACT_APP_SERVER_URL || '';
};

/**
 * Build full image URL from relative path
 * @param {string} relativePath - Path like /uploads/..., /images/..., or full URL
 * @returns {string} Full URL or empty string
 */
export const getImageUrl = (relativePath) => {
  if (!relativePath) return '';
  
  // Already a full URL (Bunny URLs, external URLs)
  if (relativePath.startsWith('http')) {
    return relativePath;
  }
  
  // Bunny Storage paths - use direct Bunny URL
  if (relativePath.startsWith('/images/')) {
    // Remove leading slash and construct Bunny Storage URL
    const bunnyPath = relativePath.substring(1); // Remove /
    const bunnyHostname = process.env.REACT_APP_BUNNY_HOSTNAME;
    
    // ⚠️ Critical: Bunny hostname MUST be configured
    if (!bunnyHostname || bunnyHostname === 'your-bunny-hostname.b-cdn.net') {
      // Return empty URL instead of broken URL
      return '';
    }
    
    const fullUrl = `https://${bunnyHostname}/${bunnyPath}`;
    return fullUrl;
  }
  
  // Local fallback paths (/uploads/...)
  const baseUrl = getServerUrl();
  if (!baseUrl) {
    return '';
  }
  
  const fullUrl = `${baseUrl}${relativePath.startsWith('/') ? relativePath : '/' + relativePath}`;
  
  return fullUrl;
};

/**
 * Build full payment screenshot URL
 * @param {string} screenshotPath - Path to screenshot
 * @returns {string} Full URL or empty string
 */
export const getPaymentScreenshotUrl = (screenshotPath) => {
  if (!screenshotPath) return '';
  
  const baseUrl = getServerUrl();

if (!baseUrl) {
  return '';
}
  // If it's already a full URL, return as-is
  if (screenshotPath.startsWith('http')) return screenshotPath;

  // Bunny image paths (generated by backend) start with /images/
  if (screenshotPath.startsWith('/images/')) {
    // Delegate to getImageUrl which handles Bunny hostname validation
    return getImageUrl(screenshotPath);
  }

  // If it's already a full uploads path (starts with /uploads), use server base
  if (screenshotPath.startsWith('/uploads/')) {
    return `${baseUrl}${screenshotPath}`;
  }

  // Default: treat value as filename stored in /uploads/payment-screenshots/
  // This maintains backward compatibility with legacy screenshots
  return `${baseUrl}/uploads/payment-screenshots/${screenshotPath}`;
};

// Export constants for backward compatibility
export const API_BASE_URL = getApiUrl();
export const SERVER_BASE_URL = getServerUrl();


const apiConfig = {
  getApiUrl,
  getServerUrl,
  getImageUrl,
  getPaymentScreenshotUrl,
  API_BASE_URL,
  SERVER_BASE_URL,
  isDev,
  isProduction
};

export default apiConfig;
